import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { api } from '../services/api';
import { formatEnrollmentDeadline } from '../utils/dateUtils';
import {
  Box, Container, Card, CardContent, CardActions, Button, Typography,
  CircularProgress, Alert, Chip
} from '@mui/material';
import {
  Schedule as ScheduleIcon, AttachMoney as MoneyIcon,
  School as SchoolIcon, Event as EventIcon
} from '@mui/icons-material';

// Lowest advertised price for a program (per-role map min, or flat price).
const displayPrice = (p) => {
  if (p.feature_per_role_pricing && p.price_map) {
    const vals = Object.values(p.price_map).filter((v) => typeof v === 'number');
    if (vals.length) return Math.min(...vals);
  }
  return p.price;
};

function Programs() {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => { fetchPrograms(); }, []);

  const fetchPrograms = async () => {
    try {
      setLoading(true);
      setError('');
      setPrograms((await api.getPrograms().catch(() => [])) || []);
    } catch (err) {
      console.error('Error fetching programs:', err);
      setError('Failed to load programs. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const go = (program) => navigate(`/programs/${program.id}`);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>Loading programs...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pt: 12, pb: 4 }}>
      <Container maxWidth="lg">
        <Typography variant="h3" component="h2" sx={{ fontWeight: 700, mb: 1 }}>Youth Programs</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>Explore our engaging programs for youth development</Typography>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {programs.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'background.paper', borderRadius: 2 }}>
            <SchoolIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">No programs available at this time.</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'flex-start' }}>
            {programs.map((program) => {
              const price = displayPrice(program);
              const deadlinePassed = program.enrollment_deadline && new Date(program.enrollment_deadline) < new Date();
              const closed = !program.enrollment_open || deadlinePassed;
              return (
                <Card
                  key={program.id}
                  sx={{
                    width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33.333% - 16px)' },
                    maxWidth: 400, display: 'flex', flexDirection: 'column', cursor: 'pointer',
                    transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' }
                  }}
                  onClick={() => go(program)}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                      <Typography variant="h5" component="h3" sx={{ fontWeight: 600, flex: 1, pr: 1 }}>{program.name}</Typography>
                      {price != null && <Chip icon={<MoneyIcon />} label={`$${price}`} color="primary" size="small" />}
                    </Box>

                    <Box sx={{ mb: 3, minHeight: '3em',
                      '& p': { mb: 0.5, fontSize: '0.875rem', color: 'text.secondary' },
                      '& ul, & ol': { pl: 2, mb: 0.5 }, '& li': { fontSize: '0.875rem', color: 'text.secondary' } }}>
                      {program.feature_markdown ? (
                        <ReactMarkdown>{program.description || ''}</ReactMarkdown>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{program.description}</Typography>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {program.start_date && program.end_date && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ScheduleIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                          <Typography variant="body2" color="text.secondary">
                            <strong>Dates:</strong> {formatDate(program.start_date)} - {formatDate(program.end_date)}
                          </Typography>
                        </Box>
                      )}
                      {program.enrollment_deadline && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EventIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                          <Typography variant="body2" color="text.secondary">
                            <strong>Deadline:</strong> {formatEnrollmentDeadline(program.enrollment_deadline)}
                          </Typography>
                        </Box>
                      )}
                      {(program.male_capacity > 0 || program.female_capacity > 0) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SchoolIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                          <Typography variant="body2" color="text.secondary">
                            <strong>Capacity:</strong> {program.male_capacity || 0} Male / {program.female_capacity || 0} Female
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>

                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button fullWidth variant="contained" onClick={() => go(program)}>
                      {closed ? 'View Details (Enrollment Closed)' : 'View Details & Enroll'}
                    </Button>
                  </CardActions>
                </Card>
              );
            })}
          </Box>
        )}
      </Container>
    </Box>
  );
}

export default Programs;
